import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Type } from '@angular/core';
import { Subscription } from 'rxjs';
import { IContentRenderer, GroupPanelPartInitParameters } from 'dockview-core';
import { PanelDescriptor } from './window-manager';
import { WidgetService } from '../widget.service';

/**
 * Bridges dockview's vanilla DOM panel rendering with Angular components.
 *
 * For each panel, dockview calls `createComponent()` which returns an
 * IContentRenderer. Our renderer creates a host <div>, mounts the Angular
 * widget component into it via `createComponent()`, and wires inputs/outputs.
 */
export class AngularPanelRenderer implements IContentRenderer {
  readonly element: HTMLElement;
  private _componentRef: ComponentRef<any> | null = null;
  private _outputSubs: Subscription[] = [];

  constructor(
    private _injector: EnvironmentInjector,
    private _appRef: ApplicationRef
  ) {
    this.element = document.createElement('div');
    this.element.style.height = '100%';
    this.element.style.width = '100%';
    this.element.style.overflow = 'hidden';
    this.element.classList.add('dockview-panel-host');
  }

  init(params: GroupPanelPartInitParameters): void {
    const descriptor = params.params as unknown as PanelDescriptor;
    if (!descriptor?.component) return;

    this.element.setAttribute('data-widget', descriptor.component);

    const componentType = WidgetService.REGISTRY[descriptor.component as WidgetService.WidgetName] as Type<any>;
    if (!componentType) {
      console.warn(`[AngularPanelRenderer] Unknown component: ${descriptor.component} — widget may have been removed`);
      this.element.innerHTML = `<div style="padding:16px;color:#888;font-size:13px;">Widget unavailable: ${descriptor.component}</div>`;
      return;
    }

    this._componentRef = createComponent(componentType, {
      environmentInjector: this._injector,
      hostElement: this.element
    });

    // Wire inputs (same mechanism as ndc-dynamic)
    if (descriptor.inputs) {
      for (const [key, value] of Object.entries(descriptor.inputs)) {
        try {
          this._componentRef.setInput(key, value);
        } catch {
          // Some inputs may not exist on the component — skip silently
        }
      }
    }

    // Wire outputs (subscribe to EventEmitters)
    if (descriptor.outputs) {
      for (const [key, handler] of Object.entries(descriptor.outputs)) {
        const output = this._componentRef.instance[key];
        if (output?.subscribe) {
          this._outputSubs.push(output.subscribe(handler));
        }
      }
    }

    // Attach the view to Angular's change detection tree
    this._appRef.attachView(this._componentRef.hostView);
  }

  /** Re-wire outputs after layout restore (outputs don't survive JSON serialization) */
  wireOutputs(outputs: Record<string, any>): void {
    if (!this._componentRef || !outputs) return;
    for (const [key, handler] of Object.entries(outputs)) {
      const output = this._componentRef.instance[key];
      if (output?.subscribe) {
        this._outputSubs.push(output.subscribe(handler));
      }
    }
  }

  dispose(): void {
    this._outputSubs.forEach(s => s.unsubscribe());
    this._outputSubs = [];

    if (this._componentRef) {
      this._appRef.detachView(this._componentRef.hostView);
      this._componentRef.destroy();
      this._componentRef = null;
    }
  }
}
